/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as CallActionCreators from '@app/actions/CallActionCreators';
import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import {CallNotRingableModal} from '@app/components/alerts/CallNotRingableModal';
import {ConfirmModal} from '@app/components/modals/ConfirmModal';
import {Logger} from '@app/lib/Logger';
import AccessibilityStore from '@app/stores/AccessibilityStore';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

const logger = new Logger('CallUtils');

export interface CallStartConfirmationOptions {
	bypassConfirm?: boolean;
	silent?: boolean;
	kind?: 'voice' | 'video';
}

export interface CallStartConfirmationEvent {
	shiftKey?: boolean;
}

export function shouldBypassCallStartConfirmation(event?: CallStartConfirmationEvent | null): boolean {
	return Boolean(event?.shiftKey);
}

export function shouldStartCallSilently(event?: CallStartConfirmationEvent | null): boolean {
	return Boolean(event?.shiftKey);
}

export function getCallStartRequestOptions(
	event?: CallStartConfirmationEvent | null,
	options: Pick<CallStartConfirmationOptions, 'kind'> = {},
): CallStartConfirmationOptions {
	return {
		...options,
		bypassConfirm: shouldBypassCallStartConfirmation(event),
		silent: shouldStartCallSilently(event),
	};
}

export async function checkAndStartCall(channelId: string, silent = false): Promise<boolean> {
	try {
		const {ringable} = await CallActionCreators.checkCallEligibility(channelId);
		if (!ringable) {
			ModalActionCreators.push(modal(() => <CallNotRingableModal />));
			return false;
		}
		CallActionCreators.startCall(channelId, silent);
		return true;
	} catch (error) {
		logger.error('Failed to check call eligibility:', error);
		return false;
	}
}

export async function requestStartCall(
	i18n: I18n,
	channelId: string,
	options: CallStartConfirmationOptions = {},
): Promise<void> {
	const {bypassConfirm = false, kind = 'voice', silent = false} = options;
	const shouldConfirm = AccessibilityStore.confirmBeforeStartingCalls && !bypassConfirm;

	if (!shouldConfirm) {
		await checkAndStartCall(channelId, silent);
		return;
	}

	const isVideoCall = kind === 'video';
	const title = isVideoCall ? i18n._(msg`Start Video Call`) : i18n._(msg`Start Voice Call`);
	const description = isVideoCall
		? i18n._(msg`Are you sure you want to start a video call?`)
		: i18n._(msg`Are you sure you want to start a voice call?`);

	ModalActionCreators.push(
		modal(() => (
			<ConfirmModal
				title={title}
				description={description}
				primaryText={title}
				primaryVariant="primary"
				onPrimary={async () => {
					await checkAndStartCall(channelId, silent);
				}}
			/>
		)),
	);
}
